"use client";
import { useQuery } from "@livestore/react";
import type { LiveQuery } from "../LiveQuery.ts";

/**
 * Renders the value of a LiveQuery using a render function.
 * This function can use hooks like useMemo etc within
 * @example
 * ```tsx
 * <LiveValue query={isEnabled$}>
 *   {(value) => <div data-loc="phosphor-blog/content/_sources/view-model-pattern/todo-vm/components/LiveValue.tsx:11:23" data-name="div">{value ? "Enabled" : "Disabled"}</div>}
 * </LiveValue>
 * <LiveValue query={isEnabled$}>
 *   {(value) => {
 *     // Use hooks for whatever you need to avoid creating new component functions
 *     const memoizedValue = useMemo(() => value, [value]);
 *     return <div data-loc="phosphor-blog/content/_sources/view-model-pattern/todo-vm/components/LiveValue.tsx:17:20" data-name="div">{memoizedValue ? "Enabled" : "Disabled"}</div>;
 *   }}
 * }}
 * </LiveValue>
 * ```
 * @param props.query - The LiveQuery to subscribe to.
 * @param props.children - The function to render the value.
 * @returns The rendered value.
 */
export function LiveValue<T>(props: {
  query: LiveQuery<T>;
  children: (value: Awaited<T>) => React.ReactNode;
}): React.ReactNode;
/**
 * @example
 * ```tsx
 * // Render as text
 * <LiveValue query={displayName$} />
 * ```
 * @param props.query - The LiveQuery to subscribe to.
 * @returns The rendered value.
 */
export function LiveValue(props: {
  query: LiveQuery<React.ReactNode | string | number | boolean | null | undefined>;
}): React.ReactNode;
export function LiveValue(props: {
  query: LiveQuery<any>;
  children?: (value: any) => React.ReactNode;
}) {
  const value = useQuery(props.query as any);
  return <>{typeof props.children === "function" ? props.children(value) : (value as any)}</>;
}
